import { ChevronRightSolid, StarRegular, StarSolid } from '@allocab/allocab-icons';
import { TripOutput } from '@allocab/allocab-passenger-shared-types';
import type { NoteTripInput } from '@allocab/allocab-passenger-shared-types';
import { useState } from 'react';

import trustPilotImage from '../../assets/images/trustpilot.svg';
import { PrimaryButton } from '../../components/button/PrimaryButton';
import { constants } from '../../constants';
import { t } from '../../services/translation/translation';

interface Props {
  trip: TripOutput;
  onCreateNote?: (_input: NoteTripInput) => void;
  isLoading: boolean;
}

export const TripRating = ({ trip, onCreateNote, isLoading }: Props) => {
  const [note, setNote] = useState(0 as number);
  const [securityIssue, setSecurityIssue] = useState(false);
  const [comfortIssue, setComfortIssue] = useState(false);
  const [timingIssue, setTimingIssue] = useState(false);
  const [courtesyIssue, setCourtesyIssue] = useState(false);
  const [comment, setComment] = useState('');

  const getRatingText = () => {
    switch (note) {
      case 1:
        return t('share-route.trip-rating.stars.1');
      case 2:
        return t('share-route.trip-rating.stars.2');
      case 3:
        return t('share-route.trip-rating.stars.3');
      case 4:
        return t('share-route.trip-rating.stars.4');
      case 5:
        return t('share-route.trip-rating.stars.5');
    }
  };

  const handleRate = () => {
    if (onCreateNote) {
      onCreateNote({ tripKey: trip.key, note, securityIssue, comfortIssue, timingIssue, courtesyIssue, comment });
    }
  };

  return (
    <div>
      {!constants.isHotels ? (
        <div className="mb-2 flex flex-col items-center justify-center text-lg">
          <span className="text-center">{t('share-route.trip-rating.title')}</span>
          {trip.state === 'DONE' && !trip.tripVars.noted && onCreateNote ? (
            <span className="text-center">{t('share-route.trip-rating.how-was-trip')}</span>
          ) : null}
        </div>
      ) : null}
      {trip.state === 'DONE' && !trip.tripVars.noted && onCreateNote ? (
        <>
          <div className="bg-background_highlight p-5 flex-col mb-2">
            <div className="justify-evenly flex items-center">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} onClick={() => setNote(index + 1)}>
                  {note > index ? (
                    <StarSolid width={36} height={36} className="fill-primary" />
                  ) : (
                    <StarRegular width={36} height={36} className="fill-primary" />
                  )}
                </div>
              ))}
            </div>
            {note > 0 ? (
              <div className="mt-5 flex items-center justify-center uppercase font-bold">{getRatingText()}</div>
            ) : null}
          </div>
          {note > 0 && note < 5 ? (
            <>
              <div className="mb-2 uppercase flex items-center justify-center text-sm">
                {t('share-route.trip-rating.improve')}
              </div>
              <div className="flex items-center justify-between overflow-x-auto">
                <button
                  onClick={() => setSecurityIssue(v => !v)}
                  className={`${
                    securityIssue
                      ? 'bg-cta text-text_light'
                      : 'bg-background_highlight  hover:text-text_light hover:bg-cta'
                  } p-2 ml-auto`}
                >
                  {t('share-route.trip-rating.security')}
                </button>
                <button
                  onClick={() => setComfortIssue(v => !v)}
                  className={`${
                    comfortIssue
                      ? 'bg-cta text-text_light'
                      : 'bg-background_highlight  hover:text-text_light hover:bg-cta'
                  } p-2 ml-2`}
                >
                  {t('share-route.trip-rating.comfort')}
                </button>
                <button
                  onClick={() => setTimingIssue(v => !v)}
                  className={`${
                    timingIssue
                      ? 'bg-cta text-text_light'
                      : 'bg-background_highlight  hover:text-text_light hover:bg-cta'
                  } p-2 ml-2`}
                >
                  {t('share-route.trip-rating.timing')}
                </button>
                <button
                  onClick={() => setCourtesyIssue(v => !v)}
                  className={`${
                    courtesyIssue
                      ? 'bg-cta text-text_light'
                      : 'bg-background_highlight  hover:text-text_light hover:bg-cta'
                  } p-2 ml-2 mr-auto`}
                >
                  {t('share-route.trip-rating.courtesy')}
                </button>
              </div>
            </>
          ) : null}
          {note > 0 ? (
            <textarea
              placeholder={
                note === 5
                  ? (t('share-route.trip-rating.good-placeholder') as string)
                  : (t('share-route.trip-rating.bad-placeholder') as string)
              }
              onChange={e => setComment(e.target.value)}
              className="border bg-background_highlight p-2 mt-1 w-full h-24"
              value={comment}
            />
          ) : null}
          <div>
            <input type="hidden" name="note" value={note} />
            <input type="hidden" name="timingIssue" value={timingIssue ? 'true' : 'false'} />
            <input type="hidden" name="securityIssue" value={securityIssue ? 'true' : 'false'} />
            <input type="hidden" name="comfortIssue" value={comfortIssue ? 'true' : 'false'} />
            <input type="hidden" name="courtesyIssue" value={courtesyIssue ? 'true' : 'false'} />
            <input type="hidden" name="comment" value={comment} />

            <PrimaryButton onClick={handleRate} className="w-full" isLoading={isLoading}>
              {t('share-route.trip-rating.validate')}
            </PrimaryButton>
          </div>
        </>
      ) : null}
      {trip.tripVars.noted && trip.tripVars.note ? (
        <>
          <div className="bg-background_highlight p-5 flex-col">
            <div className="flex items-center justify-evenly">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index}>
                  {trip.tripVars.note && trip.tripVars.note > index ? (
                    <StarSolid width={36} height={36} className="fill-primary" />
                  ) : (
                    <StarRegular width={36} height={36} className="fill-primary" />
                  )}
                </div>
              ))}
            </div>
          </div>
          {trip.tripVars.note === 5 && !constants.isHotels ? (
            <>
              <div className="my-2 flex flex-col items-center justify-center">
                <span className="text-center">{t('share-route.trip-rating.share-experience')}</span>
              </div>
              <a target="blank" href="https://fr.trustpilot.com/evaluate/allocab.com">
                <div className="flex items-center justify-between text-lg">
                  <div className="flex items-center text-lg">
                    <img className="w-10" alt="trustpilot" src={trustPilotImage} />
                    <span className="font-bold ml-2">Trustpilot</span>
                  </div>
                  <ChevronRightSolid className="cursor-pointer fill-primary" width={20} height={20} />
                </div>
              </a>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
